import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { isValidPhoneNumber } from 'react-phone-number-input'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, DateTimePicker, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { CreatClient } from '@/common/models/clients'
import { type IClient } from '@/core/services/clients'
import { MessageBox } from '../messages/MessageBox'
import { useClients } from '@/home/hooks/use-clients'
import Loader from '@/common/components/Loader'

export interface IClientsValues {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  password: string
  birthDate: string
}

export interface CreateClientModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  page: number
  size: number
  sortBy?: string
  setTotalElemenets: (elements: number) => void
  setCLients: (c: IClient[]) => void
}

export const CreateClientModal = (
  props: CreateClientModalProps,
): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    page,
    size,
    sortBy,
    setCLients,
    setTotalElemenets,
  } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const { createClient, fetchClients, loading } = useClients()

  const schema = yup.object().shape({
    firstName: yup.string().label('First Name').required(),
    lastName: yup.string().label('Last Name').required(),
    phoneNumber: yup
      .string()
      .label('Phone Number')
      .required()
      .test('Valid phone number', 'Invalid phone number', (value) => {
        if (value) return isValidPhoneNumber(value)
        return false
      }),
    email: yup.string().email().label('Email').required(),
    password: yup.string().label('Password').required(),
    birthDate: yup.string().label('Birthdate').required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    control,
    setError,
  } = useForm<IClientsValues>({
    mode: 'onChange',
    resolver: yupResolver<IClientsValues>(schema),
  })

  const handleClose = () => {
    reset()
    setShowCreateModal(false)
  }

  const submit = async (values: IClientsValues) => {
    try {
      const data: CreatClient = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        password: values.password,
        birthDate: new Date(values.birthDate).valueOf(),
      }

      await createClient(data)

      const response2 = await fetchClients({
        page,
        size,
        sortBy,
      })

      setCLients(response2.content)
      setTotalElemenets(response2.totalElements)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Create Successful')
      handleClose()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.response?.status === 409) {
        setError('email', {
          type: 'manual',
          message: 'This email address is already registered',
        })
        setError('phoneNumber', {
          type: 'manual',
          message: 'This phone number is already registered',
        })
      } else {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    }
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        size="lg"
        title={<PageTitle title="Add Client" />}
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button
              form="create-client-form"
              type="submit"
              disabled={loading || Object.keys(dirtyFields).length === 0}
              loading={loading}
            >
              Save
            </Button>
          </div>
        }
      >
        {loading && <Loader />}
        <form
          id="create-client-form"
          onSubmit={handleSubmit(submit)}
          className="flex flex-col gap-4"
        >
          <FormGroup label="First Name" error={errors?.firstName?.message}>
            <Input
              isInvalid={!!errors?.firstName?.message}
              {...register('firstName')}
              type="text"
              placeholder="Enter First Name"
            />
          </FormGroup>
          <FormGroup label="Last Name" error={errors?.lastName?.message}>
            <Input
              isInvalid={!!errors?.lastName?.message}
              {...register('lastName')}
              type="text"
              placeholder="Enter Last Name"
            />
          </FormGroup>
          <FormGroup label="Phone Number" error={errors?.phoneNumber?.message}>
            <Input
              isInvalid={!!errors?.phoneNumber?.message}
              {...register('phoneNumber')}
              type="text"
              placeholder="Enter Phone Number"
            />
          </FormGroup>
          <FormGroup label="Email" error={errors?.email?.message}>
            <Input
              isInvalid={!!errors?.email?.message}
              {...register('email')}
              type="text"
              placeholder="Enter Email"
            />
          </FormGroup>
          <FormGroup label="Enter Password" error={errors?.password?.message}>
            <Input
              isInvalid={!!errors?.password?.message}
              {...register('password')}
              type="password"
              placeholder="Enter Password"
            />
          </FormGroup>
          <Controller
            name="birthDate"
            control={control}
            render={({ field }) => (
              <FormGroup label="Birthdate" error={errors?.birthDate?.message}>
                <DateTimePicker
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => {
                    field.onChange(date ? date.toISOString() : '')
                  }}
                  mode="date"
                  placeholder="Enter Birthdate"
                  hasError={!!errors?.birthDate?.message}
                />
              </FormGroup>
            )}
          />
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
